import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useGetTenant } from '../hooks/useAdmin';

/**
 * Tenant Context for managing the active tenant scope across the application.
 * - ADMIN/USER roles: derives tenantId from AuthContext, fetches tenant name on mount.
 * - PLATFORM_ADMIN role: starts with null, updated via setActiveTenant (Tenant Switcher).
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 10.7, 10.8
 */

interface TenantContextType {
  activeTenantId: string | null;
  activeTenantName: string | null;
  setActiveTenant: (id: string | null, name: string | null) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantContextProviderProps {
  children: ReactNode;
}

export const TenantContextProvider: React.FC<TenantContextProviderProps> = ({ children }) => {
  const { currentUser, isPlatformAdmin } = useAuth();
  const { data: tenantData, fetchTenant } = useGetTenant();

  // Platform Admin: managed via setActiveTenant; others: derived from AuthContext
  const [platformTenantId, setPlatformTenantId] = useState<string | null>(null);
  const [platformTenantName, setPlatformTenantName] = useState<string | null>(null);

  // For ADMIN/USER roles, fetch tenant name when tenantId is available.
  // For PLATFORM_ADMIN, also fetch their default tenant (Docari) on login.
  useEffect(() => {
    if (!isPlatformAdmin && currentUser?.tenantId) {
      fetchTenant(currentUser.tenantId);
    }
  }, [isPlatformAdmin, currentUser?.tenantId, fetchTenant]);

  // Initialize Platform Admin's active tenant to their own tenantId (Docari) on login
  useEffect(() => {
    if (isPlatformAdmin && currentUser?.tenantId && platformTenantId === null) {
      fetchTenant(currentUser.tenantId);
      setPlatformTenantId(currentUser.tenantId);
    }
  }, [isPlatformAdmin, currentUser?.tenantId, platformTenantId, fetchTenant]);

  // Sync tenant name once fetched
  useEffect(() => {
    if (isPlatformAdmin && tenantData?.name && platformTenantId === tenantData.tenantId) {
      setPlatformTenantName(tenantData.name);
    }
  }, [isPlatformAdmin, tenantData, platformTenantId]);

  const setActiveTenant = useCallback((id: string | null, name: string | null) => {
    setPlatformTenantId(id);
    setPlatformTenantName(name);
  }, []);

  let activeTenantId: string | null;
  let activeTenantName: string | null;

  if (isPlatformAdmin) {
    activeTenantId = platformTenantId;
    activeTenantName = platformTenantName;
  } else {
    activeTenantId = currentUser?.tenantId ?? null;
    activeTenantName = tenantData?.name ?? null;
  }

  return (
    <TenantContext.Provider value={{ activeTenantId, activeTenantName, setActiveTenant }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenantContext = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenantContext must be used within a TenantContextProvider');
  }
  return context;
};
